import { useCallback } from 'react';
import { type DropEvent, type FileRejection, useDropzone } from 'react-dropzone';

import { useToasts } from '@air/provider-toast';

import {
  INVALID_VISUAL_SEARCH_IMAGE_TOAST,
  type UseProcessVisualSearchImageParams,
  useProcessVisualSearchImage,
} from '~/components/Search/hooks/useProcessVisualSearchImage';
import { IMAGE_SEARCH_DROPZONE_ACCEPT } from '~/utils/imageSearch';

export type UseVisualSearchDropzoneParams = UseProcessVisualSearchImageParams;

export const useVisualSearchDropzone = ({ errorContext, onSearch }: UseVisualSearchDropzoneParams) => {
  const { showToast } = useToasts();
  const { isProcessing, processVisualSearchImage } = useProcessVisualSearchImage({ errorContext, onSearch });

  const handleDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      // Nested in the board upload dropzone; keep a file *drop* from also starting an upload.
      // Browse files uses the file picker — react-dropzone then calls onDrop with a null event.
      event?.stopPropagation();
      event?.preventDefault();

      if (fileRejections.length > 0) {
        showToast(INVALID_VISUAL_SEARCH_IMAGE_TOAST, { color: 'red' });
        return;
      }

      const [file] = acceptedFiles;

      if (file) {
        await processVisualSearchImage(file);
      }
    },
    [processVisualSearchImage, showToast],
  );

  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: IMAGE_SEARCH_DROPZONE_ACCEPT,
    maxFiles: 1,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
  });

  const openVisualSearchFilePicker = useCallback(() => {
    open();
  }, [open]);

  return {
    getInputProps,
    getRootProps,
    isDragActive,
    isProcessing,
    openVisualSearchFilePicker,
  };
};
